import { zodResolver } from '@hookform/resolvers/zod';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { InsInsightOutcomeResponse } from '@plato/data/prisma';
// RETAIN-131: the context-tag chips have no @plato/ui equivalent (its Button only
// exposes primary/secondary), so they stay on the legacy quick-filter button.
import { Button as QuickFilterButton } from '@plato/ui-legacy/components/ui/button';
import Text from '@plato/ui-legacy/components/ui/text';
import {
    DialogBody,
    DialogFooter,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
} from '@plato/ui/containers/dialog';
import { Button } from '@plato/ui/primitives/button';
import { Spinner } from '@plato/ui/primitives/spinner';
import { Typography } from '@plato/ui/primitives/typography';
import { cn } from '@plato/ui/util/classes';
import type { ReactNode } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { getInsightName } from '../../features/insights-common/lib/insight-name.ts';
import { RichTextInput } from '../../features/rich-text/components/rich-text-input.tsx';
import type { InsightDTO } from '../../server/dto/insight-dto.ts';
import { richTextSchema } from '../../server/types/common-types.ts';

const formSchema = z.object({
    response: z.enum(InsInsightOutcomeResponse),
    textRich: richTextSchema.nullable(),
    contextTags: z.array(z.string()),
});

export type InsightOutcomeNotRelevantFormValues = z.infer<typeof formSchema>;

type InsightOutcomeNotRelevantFormProps = {
    insight: InsightDTO;
    estimatedPotential: string;
    productCategoryTitle: string;
    /** Whether an external submit is in flight — drives the submit button's loading/disabled state. */
    isSubmitting?: boolean;
    /** Presentational form: it gathers and validates the outcome, the caller performs the submission. */
    onSubmit: (data: InsightOutcomeNotRelevantFormValues) => void;
    onClose: () => void;
};

export function InsightOutcomeNotRelevantForm({
    insight,
    estimatedPotential,
    productCategoryTitle,
    isSubmitting = false,
    onSubmit,
    onClose,
}: InsightOutcomeNotRelevantFormProps): ReactNode {
    const { t } = useTranslation();
    const { enableNeverBoughtCrossSellCopy } = useAppConfig();

    // "Merchandise group is incorrect" needs a merchandise group to be wrong about, so an insight
    // actionable on the customer itself (`GlobalChurnWarning`) doesn't offer it (RETAIN-813).
    const hasCategory = insight.actionable_entity_type === 'WEB_CATEGORY';

    const {
        handleSubmit,
        control,
        setValue,
        formState: { isValid },
    } = useForm<InsightOutcomeNotRelevantFormValues>({
        defaultValues: {
            response: InsInsightOutcomeResponse.NORMAL,
            textRich: null,
            contextTags: [],
        },
        resolver: zodResolver(formSchema),
        mode: 'onChange',
    });

    const selectedResponse = useWatch({
        control,
        name: 'response',
        defaultValue: InsInsightOutcomeResponse.NORMAL,
    });

    const getPlaceholder = (): string => {
        if (selectedResponse === InsInsightOutcomeResponse.INCORRECT) {
            return t('outcome_context__placeholder_not_relevant__INCORRECT', { ns: 'insights' });
        }

        return t('outcome_context__placeholder_not_relevant__NORMAL', { ns: 'insights' });
    };

    return (
        <>
            <DialogHeader>
                <div className="flex min-w-0 flex-1 flex-col gap-tight">
                    <DialogTitle>{t('outcome_modal__title_not_relevant', { ns: 'insights' })}</DialogTitle>
                    <Typography variant="body" className="text-content-weak tabular-nums">
                        {productCategoryTitle} •{' '}
                        {getInsightName(t, insight, { neverBoughtCrossSell: enableNeverBoughtCrossSellCopy })}{' '}
                        • {estimatedPotential} {t('outcome_modal__est_value', { ns: 'insights' })}
                    </Typography>
                </div>
                <DialogIconClose label={t('form__close', { ns: 'common' })} />
            </DialogHeader>
            <DialogBody className="flex flex-col gap-4">
                <div className="flex flex-col gap-3 bg-neutral-50 rounded-lg p-3">
                    <Text size="sm" weight="medium" color="neutral-700">
                        {t('outcome_modal__question_not_relevant', { ns: 'insights' })}
                    </Text>

                    <Controller
                        name="response"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <div className="flex flex-col gap-2">
                                <button
                                    key={InsInsightOutcomeResponse.NORMAL}
                                    type="button"
                                    onClick={() => {
                                        onChange(InsInsightOutcomeResponse.NORMAL);
                                        setValue('contextTags', []);
                                    }}
                                    className={cn(
                                        'flex items-start gap-2 rounded-lg border border-neutral-200 p-3 text-left bg-white',
                                    )}
                                >
                                    <span
                                        className={cn(
                                            'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                                            value === InsInsightOutcomeResponse.NORMAL
                                                ? 'border-blue-500'
                                                : 'border-neutral-300',
                                        )}
                                    >
                                        {value === InsInsightOutcomeResponse.NORMAL && (
                                            <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
                                        )}
                                    </span>
                                    <div className="flex flex-col gap-1">
                                        <Text size="sm" weight="medium" color="neutral-950">
                                            {t('outcome_response__NORMAL', {
                                                ns: 'insights',
                                            })}
                                        </Text>
                                        <Text size="sm" color="neutral-500">
                                            {t('outcome_response__NORMAL__description', {
                                                ns: 'insights',
                                            })}
                                        </Text>
                                    </div>
                                </button>
                                {hasCategory && (
                                    <button
                                        key={InsInsightOutcomeResponse.INCORRECT}
                                        type="button"
                                        onClick={() => {
                                            onChange(InsInsightOutcomeResponse.INCORRECT);
                                            setValue('contextTags', []);
                                        }}
                                        className={cn(
                                            'flex items-start gap-2 rounded-lg border border-neutral-200 p-3 text-left bg-white',
                                        )}
                                    >
                                        <span
                                            className={cn(
                                                'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                                                value === InsInsightOutcomeResponse.INCORRECT
                                                    ? 'border-blue-500'
                                                    : 'border-neutral-300',
                                            )}
                                        >
                                            {value === InsInsightOutcomeResponse.INCORRECT && (
                                                <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
                                            )}
                                        </span>
                                        <div className="flex flex-col gap-1">
                                            <Text size="sm" weight="medium" color="neutral-950">
                                                {t('outcome_response__INCORRECT', {
                                                    ns: 'insights',
                                                })}
                                            </Text>
                                            <Text size="sm" color="neutral-500">
                                                {t('outcome_response__INCORRECT__description', {
                                                    ns: 'insights',
                                                })}
                                            </Text>
                                        </div>
                                    </button>
                                )}
                            </div>
                        )}
                    />
                </div>
                <div className="flex flex-col gap-3 pt-3">
                    <Text size="sm" weight="medium" color="neutral-700">
                        {t('outcome_context__label', { ns: 'insights' })}
                    </Text>
                    {selectedResponse === InsInsightOutcomeResponse.NORMAL && (
                        <Controller
                            name="contextTags"
                            control={control}
                            render={({ field: { onChange, value } }) => {
                                const toggleTag = (tagText: string): void => {
                                    onChange(
                                        value.includes(tagText)
                                            ? value.filter((v) => v !== tagText)
                                            : [...value, tagText],
                                    );
                                };

                                return (
                                    <div className="flex gap-2 overflow-x-auto scrollbar-none md:flex-wrap">
                                        <QuickFilterButton
                                            key="outcome_context__tag_seasonal"
                                            type="button"
                                            variant="quick-filter"
                                            className="shrink-0 rounded-4xl border border-neutral-200 bg-neutral-25 px-4 text-sm font-medium text-neutral-950"
                                            selected={value.includes(
                                                t('outcome_context__tag_seasonal', { ns: 'insights' }),
                                            )}
                                            onClick={() => {
                                                toggleTag(
                                                    t('outcome_context__tag_seasonal', {
                                                        ns: 'insights',
                                                    }),
                                                );
                                            }}
                                        >
                                            {t('outcome_context__tag_seasonal', { ns: 'insights' })}
                                        </QuickFilterButton>
                                        <QuickFilterButton
                                            key="outcome_context__tag_projects_only"
                                            type="button"
                                            variant="quick-filter"
                                            className="shrink-0 rounded-4xl border border-neutral-200 bg-neutral-25 px-4 text-sm font-medium text-neutral-950"
                                            selected={value.includes(
                                                t('outcome_context__tag_projects_only', {
                                                    ns: 'insights',
                                                }),
                                            )}
                                            onClick={() => {
                                                toggleTag(
                                                    t('outcome_context__tag_projects_only', {
                                                        ns: 'insights',
                                                    }),
                                                );
                                            }}
                                        >
                                            {t('outcome_context__tag_projects_only', { ns: 'insights' })}
                                        </QuickFilterButton>
                                        <QuickFilterButton
                                            key="outcome_context__tag_promotions_only"
                                            type="button"
                                            variant="quick-filter"
                                            className="shrink-0 rounded-4xl border border-neutral-200 bg-neutral-25 px-4 text-sm font-medium text-neutral-950"
                                            selected={value.includes(
                                                t('outcome_context__tag_promotions_only', {
                                                    ns: 'insights',
                                                }),
                                            )}
                                            onClick={() => {
                                                toggleTag(
                                                    t('outcome_context__tag_promotions_only', {
                                                        ns: 'insights',
                                                    }),
                                                );
                                            }}
                                        >
                                            {t('outcome_context__tag_promotions_only', {
                                                ns: 'insights',
                                            })}
                                        </QuickFilterButton>
                                    </div>
                                );
                            }}
                        />
                    )}
                    {selectedResponse === InsInsightOutcomeResponse.INCORRECT && (
                        <Controller
                            name="contextTags"
                            control={control}
                            render={({ field: { onChange, value } }) => {
                                const toggleTag = (tagText: string): void => {
                                    onChange(
                                        value.includes(tagText)
                                            ? value.filter((v) => v !== tagText)
                                            : [...value, tagText],
                                    );
                                };

                                return (
                                    <div className="flex gap-2 overflow-x-auto scrollbar-none md:flex-wrap">
                                        <QuickFilterButton
                                            key="outcome_context__tag_too_broad"
                                            type="button"
                                            variant="quick-filter"
                                            className="shrink-0 rounded-4xl border border-neutral-200 bg-neutral-25 px-4 text-sm font-medium text-neutral-950"
                                            selected={value.includes(
                                                t('outcome_context__tag_too_broad', { ns: 'insights' }),
                                            )}
                                            onClick={() => {
                                                toggleTag(
                                                    t('outcome_context__tag_too_broad', {
                                                        ns: 'insights',
                                                    }),
                                                );
                                            }}
                                        >
                                            {t('outcome_context__tag_too_broad', { ns: 'insights' })}
                                        </QuickFilterButton>
                                        <QuickFilterButton
                                            key="outcome_context__tag_too_specific"
                                            type="button"
                                            variant="quick-filter"
                                            className="shrink-0 rounded-4xl border border-neutral-200 bg-neutral-25 px-4 text-sm font-medium text-neutral-950"
                                            selected={value.includes(
                                                t('outcome_context__tag_too_specific', {
                                                    ns: 'insights',
                                                }),
                                            )}
                                            onClick={() => {
                                                toggleTag(
                                                    t('outcome_context__tag_too_specific', {
                                                        ns: 'insights',
                                                    }),
                                                );
                                            }}
                                        >
                                            {t('outcome_context__tag_too_specific', { ns: 'insights' })}
                                        </QuickFilterButton>
                                        <QuickFilterButton
                                            key="outcome_context__tag_dummy_category"
                                            type="button"
                                            variant="quick-filter"
                                            className="shrink-0 rounded-4xl border border-neutral-200 bg-neutral-25 px-4 text-sm font-medium text-neutral-950"
                                            selected={value.includes(
                                                t('outcome_context__tag_dummy_category', {
                                                    ns: 'insights',
                                                }),
                                            )}
                                            onClick={() => {
                                                toggleTag(
                                                    t('outcome_context__tag_dummy_category', {
                                                        ns: 'insights',
                                                    }),
                                                );
                                            }}
                                        >
                                            {t('outcome_context__tag_dummy_category', { ns: 'insights' })}
                                        </QuickFilterButton>
                                    </div>
                                );
                            }}
                        />
                    )}

                    <Controller
                        name="textRich"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <RichTextInput
                                trackingContext={{ source: 'sales_insight_outcome' }}
                                placeholder={getPlaceholder()}
                                value={value ?? undefined}
                                onChange={onChange}
                            />
                        )}
                    />
                </div>
            </DialogBody>
            <DialogFooter>
                <Button variant="secondary" onClick={onClose}>
                    {t('outcome_modal__cancel', { ns: 'insights' })}
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit(onSubmit)}
                    disabled={!isValid || isSubmitting}
                >
                    {isSubmitting && <Spinner />}
                    {t('outcome_modal__submit', { ns: 'insights' })}
                </Button>
            </DialogFooter>
        </>
    );
}
